import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SEOHead from "../components/shared/SEOHead.jsx";
import JsonLd from "../components/shared/JsonLd.jsx";
import { buildWebSiteJsonLd } from "../utils/jsonld.js";
import ResponsiveImage from "../components/shared/ResponsiveImage.jsx";
import { larpPages, getEventsForLang, getLatestPastEvent, findByLangAndId } from "../utils/content.js";
import NewsletterForm from "../components/newsletter/NewsletterForm.jsx";
import SponsorLogos from "../components/shared/SponsorLogos.jsx";
import imageManifest from "../data/imageManifest.json";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const SPENDEN_PATH = { de: "spenden", en: "donate" };
const ABOUT_PATH = { de: "ueber-uns", en: "about" };
const ORGAS_PATH = { de: "fuer-orgas", en: "for-organizers" };
const JUGENDARBEIT_PATH = { de: "jugendarbeit", en: "youth-work" };
const FREE_TICKET_SLUG = { de: "freiplatz-fonds-2026", en: "free-ticket-fund-2026" };

/** The beginner reading path, in the order a newcomer should meet it —
 * language-neutral article `id`s, so each language renders its own slug
 * and title (§ T-13). */
const START_HERE_IDS = [
  "was-ist-larp",
  "community-und-vorbereitung",
  "gewandung-und-ausruestung",
  "waffen-und-sicherheit",
  "charaktererstellung",
  "faq",
];

const HERO_IMAGE_SRC = "/assets/brand/start_hero.png";
const HERO_IMAGE_SIZES = "100vw";

/** Mirrors ResponsiveImage.jsx's own <picture> srcSet building, but for
 * exactly one format: AVIF, since it's listed first in ResponsiveImage's
 * <source> order and every AVIF-capable browser (virtually all current
 * ones, including Lighthouse's emulated Chrome) picks it there over WebP.
 *
 * Declaring a *second* preload for WebP (belt-and-suspenders for older
 * browsers) was tried and reverted: `type` on rel=preload only skips the
 * fetch for browsers that DON'T support that type, not ones that support
 * it but wouldn't otherwise choose it — real-browser network trace
 * confirmed Chromium fetched *both* the AVIF and WebP variants even
 * though <picture> only ever renders the AVIF one, wasting bandwidth on
 * exactly the congested mobile connection this preload is meant to help.
 * A pre-AVIF browser simply loses the preload's head start and falls
 * back to <picture>'s normal (still correct, just unhurried) WebP fetch. */
function heroImagePreloadLink() {
  const entry = imageManifest[HERO_IMAGE_SRC];
  if (!entry?.avif?.length) return null;
  return (
    <link
      rel="preload"
      as="image"
      type="image/avif"
      imagesrcset={entry.avif.map((v) => `${v.path} ${v.width}w`).join(", ")}
      imagesizes={HERO_IMAGE_SIZES}
      fetchpriority="high"
    />
  );
}

function formatEventDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Home({ lang }) {
  const { t } = useTranslation();
  const upcomingEvents = getEventsForLang(lang)
    .filter((e) => e.frontmatter.status === "upcoming")
    .slice(0, 2);
  const startHerePages = START_HERE_IDS.map((id) => findByLangAndId(larpPages, lang, id)).filter(Boolean);
  const latestPastEvent = getLatestPastEvent(lang);

  return (
    <>
      <SEOHead
        lang={lang}
        path="/"
        title={t("home.metaTitle")}
        description={t("home.metaDescription")}
        image={HERO_IMAGE_SRC}
        imageAlt={t("home.heroImageAlt")}
      />
      <Helmet>{heroImagePreloadLink()}</Helmet>
      <JsonLd data={buildWebSiteJsonLd()} />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "64px", paddingTop: "40px", paddingBottom: "96px" }}>
          <section style={{ position: "relative", borderRadius: "14px", overflow: "hidden" }}>
            <ResponsiveImage
              src={HERO_IMAGE_SRC}
              alt={t("home.heroImageAlt")}
              priority
              sizes={HERO_IMAGE_SIZES}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "linear-gradient(90deg, rgba(11,9,8,0.94) 0%, rgba(11,9,8,0.8) 42%, rgba(11,9,8,0.35) 100%)",
              }}
            />
            <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: "22px", padding: "72px 44px" }}>
              <p className="tl-eyebrow" style={{ margin: 0 }}>
                {t("home.heroEyebrow")}
              </p>
              <h1
                className="tl-hero-h1"
                style={{
                  fontFamily: "var(--font-serif)",
                  fontSize: "48px",
                  lineHeight: 1.06,
                  color: "var(--color-cream)",
                  maxWidth: "22ch",
                  margin: 0,
                }}
              >
                {t("home.heroHeadline")}
              </h1>
              <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "54ch", margin: 0 }}>
                {t("home.heroBody")}
              </p>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "8px" }}>
                <Link to={`/${lang}/events`} className="tl-pill tl-pill-solid">
                  {t("home.heroCtaEvents")}
                </Link>
                <Link to={`/${lang}/${ABOUT_PATH[lang]}`} className="tl-pill tl-pill-outline">
                  {t("home.heroCtaUeber")}
                </Link>
              </div>
            </div>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
              {t("home.whatWeDoTitle")}
            </h2>
            <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 }}>
              {t("home.whatWeDoBody")}
            </p>
          </section>

          <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: "20px" }}>
            {[
              { title: t("home.feature1Title"), body: t("home.feature1Body"), to: `/${lang}/${ABOUT_PATH[lang]}/${ORGAS_PATH[lang]}` },
              { title: t("home.feature2Title"), body: t("home.feature2Body"), to: `/${lang}/${SPENDEN_PATH[lang]}/${FREE_TICKET_SLUG[lang]}` },
              { title: t("home.feature3Title"), body: t("home.feature3Body"), to: `/${lang}/${JUGENDARBEIT_PATH[lang]}` },
            ].map((feature) => (
              <Link
                key={feature.title}
                to={feature.to}
                className="tl-card tl-hover-raise"
                style={{ display: "flex", flexDirection: "column", gap: "10px" }}
              >
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)" }}>
                  {feature.title}
                </span>
                <span style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)" }}>{feature.body}</span>
              </Link>
            ))}
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
                {t("home.eventsTeaserTitle")}
              </h2>
              <Link to={`/${lang}/events`} style={{ fontSize: "14px", color: "var(--color-gold)" }}>
                {t("home.allEventsLink")}
              </Link>
            </div>
            {upcomingEvents.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "24px",
                  flexWrap: "wrap",
                  background: "var(--color-bg-raised)",
                  border: "1px solid rgba(254,250,239,0.12)",
                  borderRadius: "12px",
                  padding: "24px 28px",
                }}
              >
                <p style={{ color: "var(--color-cream-soft)", margin: 0, maxWidth: "56ch" }}>
                  {lang === "de"
                    ? "Die nächste Taverne ist in Planung. Wir spielen regelmäßig im Raum Dresden — Termin und Ort kündigen wir zuerst im Newsletter an."
                    : "The next tavern night is in the works. We play regularly in the Dresden area — we announce the date and place in our newsletter first."}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  {latestPastEvent && (
                    <Link
                      to={`/${lang}/events/${latestPastEvent.frontmatter.slug}`}
                      className="tl-pill tl-pill-outline"
                    >
                      {lang === "de" ? "Letzter Tavernenabend" : "Our last tavern night"}
                    </Link>
                  )}
                  <Link to={`/${lang}/newsletter`} className="tl-pill tl-pill-primary">
                    {t("nav.newsletter")}
                  </Link>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: "24px" }}>
                {upcomingEvents.map((event) => {
                  const f = event.frontmatter;
                  return (
                    <Link
                      key={f.slug}
                      to={`/${lang}/events/${f.slug}`}
                      className="tl-card-light tl-hover-lift"
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      <div
                        className="tl-diagonal-light"
                        style={{ height: "160px", display: "flex", alignItems: "flex-end", padding: "12px" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "26px" }}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                          <span className="tl-badge">{formatEventDate(f.date, lang)}</span>
                          <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.7)" }}>{f.location}</span>
                        </div>
                        <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", lineHeight: 1.22, color: "var(--color-ink)" }}>
                          {f.title}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "6px" }}>
                          <span style={{ fontSize: "13px", color: "rgba(10,9,7,0.7)" }}>
                            {`${t("home.eventPriceFrom")} ${f.price} €`}
                          </span>
                          <span className="tl-pill tl-pill-dark" style={{ fontSize: "13px" }}>
                            {t("home.eventDetailsCta")}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>

          <SponsorLogos lang={lang} />

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("nav.newsletter")}
            </h2>
            <div className="tl-card-light tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "40px", minWidth: 0 }}>
                <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "15px", color: "#8a6520" }}>
                  {t("home.newsletterEyebrow")}
                </span>
                <p style={{ fontFamily: "var(--font-serif)", fontSize: "28px", lineHeight: 1.18, color: "var(--color-ink)", maxWidth: "24ch", margin: 0 }}>
                  {t("home.newsletterTitle")}
                </p>
                <p style={{ fontSize: "15px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", maxWidth: "46ch", margin: 0 }}>
                  {t("home.newsletterBody")}
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px", background: "rgba(10,9,7,0.05)", minWidth: 0 }}>
                <NewsletterForm lang={lang} source="Home" />
              </div>
            </div>
          </section>

          <section
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "40px",
              flexWrap: "wrap",
              border: "1px solid rgba(206,149,50,0.55)",
              borderRadius: "14px",
              padding: "36px 40px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: "1 1 420px" }}>
              <p style={{ fontFamily: "var(--font-serif)", fontSize: "28px", lineHeight: 1.2, color: "var(--color-cream)", margin: 0 }}>
                {/*
                  A literal JSX space between {expr} and <span> compiles to
                  TWO adjacent text children. A live-browser DOM snapshot
                  (this app's prerender step, not a true SSR string render)
                  has no way to preserve the node-boundary markers React's
                  own renderToString() would insert there, so hydration
                  can't tell where the first text node was meant to end and
                  reads the merged "X " string as a mismatch against the
                  expected "X". Concatenating the space into one JS string
                  keeps it as a single text child instead.
                */}
                {t("home.donateBannerTitle") + " "}
                <span style={{ color: "var(--color-gold)" }}>{t("home.donateBannerTitleHighlight")}</span>
              </p>
              <p style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-soft)", maxWidth: "60ch", margin: 0 }}>
                {t("home.donateBannerBody")}
              </p>
            </div>
            <Link to={`/${lang}/${SPENDEN_PATH[lang]}`} className="tl-pill tl-pill-primary" style={{ padding: "16px 34px", fontSize: "16px" }}>
              {t("home.donateBannerCta")}
            </Link>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "60ch" }}>
              <span className="tl-eyebrow" style={{ fontSize: "15px" }}>
                {t("home.wissenEyebrow")}
              </span>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", lineHeight: 1.2, color: "var(--color-cream)", margin: 0 }}>
                {t("home.startHereTitle")}
              </h2>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>
                {t("home.startHereBody")}
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: "20px" }}>
              {startHerePages.map((page) => (
                <Link
                  key={page.frontmatter.id}
                  to={`/${lang}/${LARP_PATH[lang]}/${page.frontmatter.slug}`}
                  className="tl-card-light tl-hover-lift"
                  style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "28px" }}
                >
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", lineHeight: 1.25, color: "var(--color-ink)" }}>
                    {page.frontmatter.metaTitle || page.frontmatter.title}
                  </span>
                  <span style={{ fontSize: "16px", lineHeight: 1.65, color: "rgba(10,9,7,0.7)" }}>
                    {page.frontmatter.subtitle}
                  </span>
                  <span style={{ fontSize: "13px", color: "var(--color-ink)", marginTop: "4px" }}>{t("larp.readMore")}</span>
                </Link>
              ))}
            </div>

            <Link to={`/${lang}/${LARP_PATH[lang]}`} style={{ fontSize: "14px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
              {t("home.allGuidesLink")}
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}
